// Synthesized WebAudio ambience/SFX engine (no external files).
// Hooks are left for real interview clips later via playClip(url).

export function createAudioEngine() {
  let ctx = null;
  let master = null;
  let ambienceNodes = null;
  let decayNodes = null;
  let muted = false;
  let volume = 0.9;
  let ducked = false;
  let duckLevel = 1;

  function ensure() {
    if (ctx) return ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : volume;
    master.connect(ctx.destination);
    return ctx;
  }

  function resume() {
    ensure();
    if (ctx && ctx.state === "suspended") ctx.resume();
  }

  function noiseBuffer(seconds = 2) {
    const len = ctx.sampleRate * seconds;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  // A single acoustic "knock" ring pulse — a low thud with a metallic ring.
  function ring() {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    g.connect(master);
    const o1 = ctx.createOscillator();
    o1.type = "sine"; o1.frequency.setValueAtTime(120, t);
    o1.frequency.exponentialRampToValueAtTime(60, t + 0.5);
    const o2 = ctx.createOscillator();
    o2.type = "triangle"; o2.frequency.setValueAtTime(320, t);
    const g2 = ctx.createGain(); g2.gain.value = 0.25; o2.connect(g2); g2.connect(g);
    o1.connect(g);
    o1.start(t); o2.start(t); o1.stop(t + 0.95); o2.stop(t + 0.4);
  }

  function alignChime() {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((f, i) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + i * 0.08);
      g.gain.exponentialRampToValueAtTime(0.22, t + i * 0.08 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.08 + 1.1);
      o.connect(g); g.connect(master); o.start(t + i * 0.08); o.stop(t + i * 0.08 + 1.15);
    });
  }

  function impactBoom() {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(1.6);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 480;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    src.connect(lp); lp.connect(g); g.connect(master);
    src.start(t); src.stop(t + 1.6);
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(70, t);
    o.frequency.exponentialRampToValueAtTime(28, t + 1.2);
    const og = ctx.createGain(); og.gain.setValueAtTime(0.8, t);
    og.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    o.connect(og); og.connect(master); o.start(t); o.stop(t + 1.4);
  }

  function startAmbience() {
    if (!ensure() || ambienceNodes) return;
    resume();
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(3); src.loop = true;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 700;
    const g = ctx.createGain(); g.gain.value = 0.05;
    src.connect(lp); lp.connect(g); g.connect(master); src.start();
    ambienceNodes = { src, g };
  }

  function stopAmbience() {
    if (ambienceNodes) { try { ambienceNodes.src.stop(); } catch (e) {} ambienceNodes = null; }
    setDecay(false);
  }

  // Low-frequency structural groans + rumble bed for the decay phase.
  function setDecay(on) {
    if (!ensure()) return;
    if (on && !decayNodes) {
      resume();
      const o = ctx.createOscillator(); o.type = "sawtooth"; o.frequency.value = 34;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 90;
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.18;
      const lfoG = ctx.createGain(); lfoG.gain.value = 0.12;
      const g = ctx.createGain(); g.gain.value = 0.0;
      g.gain.setTargetAtTime(0.16, ctx.currentTime, 2.0);
      lfo.connect(lfoG); lfoG.connect(g.gain);
      o.connect(lp); lp.connect(g); g.connect(master);
      o.start(); lfo.start();
      decayNodes = { o, lfo, g };
    } else if (!on && decayNodes) {
      try { decayNodes.o.stop(); decayNodes.lfo.stop(); } catch (e) {}
      decayNodes = null;
    }
  }

  function hazardZap() {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(0.4);
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 2400; bp.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.4, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    src.connect(bp); bp.connect(g); g.connect(master); src.start(t); src.stop(t + 0.4);
  }

  function collapseRumble() {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(4); src.loop = false;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 260;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.9, t + 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 4);
    src.connect(lp); lp.connect(g); g.connect(master); src.start(t); src.stop(t + 4);
  }

  function setMuted(m) {
    muted = m;
    if (master) master.gain.value = m ? 0 : (ducked ? volume * duckLevel : volume);
  }

  function setVolume(v) {
    volume = Math.max(0, Math.min(1, v));
    if (master && !muted) master.gain.value = ducked ? volume * duckLevel : volume;
  }

  // Duck the synthesized ambient chaos (phones/chatter/sirens/fire) so a testimony voice
  // stays clearly audible. `level` is the fraction to keep (0.6 = -40%, 0.1 = -90%).
  function duck(on, level = 0.6) {
    ducked = !!on;
    duckLevel = on ? level : 1;
    ensure();
    if (master && !muted) master.gain.setTargetAtTime(ducked ? volume * duckLevel : volume, ctx.currentTime, 0.2);
  }

  // Respiratory cough — a two-hitch filtered burst. `duck` lowers it so verbatim
  // testimony stays clearly audible over it.
  function cough(duck) {
    if (!ensure() || muted) return;
    resume();
    const t = ctx.currentTime;
    const amp = duck ? 0.1 : 0.34;
    [0, 0.17].forEach((off) => {
      const src = ctx.createBufferSource(); src.buffer = noiseBuffer(0.3);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 700; bp.Q.value = 1.2;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + off);
      g.gain.exponentialRampToValueAtTime(amp, t + off + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + off + 0.22);
      const o = ctx.createOscillator(); o.type = "sawtooth";
      o.frequency.setValueAtTime(165, t + off); o.frequency.exponentialRampToValueAtTime(80, t + off + 0.2);
      const og = ctx.createGain(); og.gain.setValueAtTime(amp * 0.5, t + off); og.gain.exponentialRampToValueAtTime(0.0001, t + off + 0.2);
      src.connect(bp); bp.connect(g); g.connect(master);
      o.connect(og); og.connect(master);
      src.start(t + off); src.stop(t + off + 0.3); o.start(t + off); o.stop(t + off + 0.25);
    });
  }

  // --- Chaos Engine hazard SFX (spatialized in-scene by event) ---
  function fireCrackle() {
    if (!ensure() || muted) return; resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(0.9);
    const bp = ctx.createBiquadFilter(); bp.type = "highpass"; bp.frequency.value = 1200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    for (let i = 0; i < 6; i++) {
      const o = t + i * 0.12 + Math.random() * 0.05;
      g.gain.exponentialRampToValueAtTime(0.2 + Math.random() * 0.12, o);
      g.gain.exponentialRampToValueAtTime(0.02, o + 0.05);
    }
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    src.connect(bp); bp.connect(g); g.connect(master); src.start(t); src.stop(t + 0.9);
  }
  function debrisThud() {
    if (!ensure() || muted) return; resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(0.5);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 300;
    const g = ctx.createGain(); g.gain.setValueAtTime(0.6, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    src.connect(lp); lp.connect(g); g.connect(master); src.start(t); src.stop(t + 0.5);
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(95, t); o.frequency.exponentialRampToValueAtTime(38, t + 0.35);
    const og = ctx.createGain(); og.gain.setValueAtTime(0.5, t); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    o.connect(og); og.connect(master); o.start(t); o.stop(t + 0.4);
  }
  function glassShatter() {
    if (!ensure() || muted) return; resume();
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(0.5);
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 3000;
    const g = ctx.createGain(); g.gain.setValueAtTime(0.32, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    src.connect(hp); hp.connect(g); g.connect(master); src.start(t); src.stop(t + 0.5);
    [5200, 6400, 7800].forEach((f, i) => {
      const o = ctx.createOscillator(); o.type = "triangle"; o.frequency.value = f;
      const og = ctx.createGain();
      og.gain.setValueAtTime(0.0001, t + i * 0.04);
      og.gain.exponentialRampToValueAtTime(0.12, t + i * 0.04 + 0.01);
      og.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.04 + 0.25);
      o.connect(og); og.connect(master); o.start(t + i * 0.04); o.stop(t + i * 0.04 + 0.3);
    });
  }

  let roarNodes = null;
  function startRoar() {
    if (!ensure() || roarNodes) return;
    resume();
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(4); src.loop = true;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 180;
    const o = ctx.createOscillator(); o.type = "sawtooth"; o.frequency.value = 40;
    const g = ctx.createGain(); g.gain.value = 0.0;
    src.connect(lp); lp.connect(g); o.connect(g); g.connect(master);
    src.start(); o.start();
    roarNodes = { src, o, g };
  }
  function setRoar(level) {
    if (roarNodes) roarNodes.g.gain.setTargetAtTime(0.28 * Math.max(0, Math.min(1, level)), ctx.currentTime, 0.4);
  }
  function stopRoar() {
    if (roarNodes) { try { roarNodes.src.stop(); roarNodes.o.stop(); } catch (e) {} roarNodes = null; }
  }

  return {
    resume, ring, alignChime, impactBoom, startAmbience, stopAmbience,
    setDecay, hazardZap, collapseRumble, setMuted, setVolume, duck, cough, startRoar, setRoar, stopRoar,
    fireCrackle, debrisThud, glassShatter,
    get isMuted() { return muted; },
    get volume() { return volume; },
  };
}
